import type { ReactNode } from 'react';

import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import FunctionsIcon from '@mui/icons-material/Functions';
import CardMedia from '@mui/material/CardMedia';

import {
  ExecutionStatusEnum,
  getFragmentData,
  graphql,
  TotalModeEnum,
} from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { useIllustrationUrl } from '../../../components/Illustrations/hooks/useIllustrationUrl.hook';
import { ReceiptIllustration } from '../../../components/Illustrations/Receipt/Receipt';

import { formatTotalMode } from '../../../utils/format-total-mode.util';
import type { ResolvableFieldSettings } from '../../ResolvableFields/types/resolvable-field-settings.type';

import type { ManualTotalForm_ResolvableServiceValue_Fragment } from '../../../forms/ManualTotal/fragments/manual-total-value.fragment';
import { TotalModeSwitch } from '../../../forms/ManualTotal/TotalModeSwitch';
import type {
  SwitchTotalModeMutation,
  UpdateManualTotalMutation,
} from '../../../forms/ManualTotal/types/props.type';

import { Avatar } from '../../Avatar';
import { CONFIDENTIAL_LABEL, ConfidentialValue } from '../../ConfidentialValue';

import { DEFAULT_SERVICE_USAGE_PLACEHOLDER_HEIGHT } from '../constants/card.constant';
import { AlertMessages } from '../../AlertMessages';

import { ServiceUsagesTotalContent } from './TotalContent';

export const ServiceUsagesTotalCardCmp_TotalCalculation_Fragment = graphql(`
  fragment ServiceUsagesTotalCardCmp_TotalCalculation_Fragment on TotalCalculation {
    __typename
    details {
      en
      fr
    }
    result {
      ...ServiceUsagesTotalCardCmp_Total_Fragment
    }
    automaticResult {
      ...ServiceUsagesTotalCardCmp_Total_Fragment
    }
    status
    totalMode
  }
`);

export const ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment = graphql(
  `
    fragment ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment on ServiceUsagesGroupTotalCalculation {
      groupId
      totalCalculation {
        ...ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
      }
    }
  `,
);

interface Props {
  readonly aside?: ReactNode;
  readonly errorMessages?: readonly string[];
  readonly title?: string;
  readonly manualTotal?:
    | FragmentType<typeof ManualTotalForm_ResolvableServiceValue_Fragment>
    | undefined
    | null;
  readonly queryKeysToInvalidate?: readonly (readonly string[])[];
  readonly resolvableFieldSettings?: ResolvableFieldSettings;
  readonly serviceUsagesGroupId?: Id;
  readonly serviceUserId?: Id;
  readonly switchTotalModeMutation?: SwitchTotalModeMutation;
  // Null when the user may not read the amounts.
  readonly totalCalculation: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
  readonly updateManualTotalMutation?: UpdateManualTotalMutation;
}

type TotalCalculationFragment =
  | FragmentType<typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment>
  | null
  | undefined;

// With a single group the global total equals the group total: it is hidden
// and its errors are shown in the group total block instead.
export const shouldShowGlobalTotal = (
  groupCount: number,
  totalCalculation: TotalCalculationFragment,
): boolean => !!totalCalculation && groupCount > 1;

export const getTotalCalculationErrorMessages = (
  totalCalculation: TotalCalculationFragment,
): readonly string[] | undefined => {
  if (!totalCalculation) {
    return undefined;
  }

  const { details, status } = getFragmentData(
    ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
    totalCalculation,
  );

  return status === ExecutionStatusEnum.Ko
    ? details.map((detail) => detail.fr)
    : undefined;
};

const DEFAULT_TITLE = 'Total';

export const ServiceUsagesTotalCard = ({
  aside,
  errorMessages,
  title = DEFAULT_TITLE,
  manualTotal,
  queryKeysToInvalidate,
  resolvableFieldSettings,
  serviceUsagesGroupId,
  serviceUserId,
  switchTotalModeMutation,
  totalCalculation: pTotalCalculation,
  updateManualTotalMutation,
}: Readonly<Props>) => {
  const imageUrl = useIllustrationUrl(ReceiptIllustration);
  const totalCalculation = pTotalCalculation
    ? getFragmentData(
        ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
        pTotalCalculation,
      )
    : null;

  // The global total has no total mode of its own: it is always the sum of the
  // group totals, i.e. computed automatically.
  const totalModeLabel = totalCalculation
    ? formatTotalMode(totalCalculation.totalMode ?? TotalModeEnum.Automatic)
    : CONFIDENTIAL_LABEL;

  return (
    <Card
      aria-label="Total des prestations"
      component="section"
      sx={{ width: '100%' }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        divider={
          <Divider
            flexItem
            orientation="vertical"
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
        }
      >
        <CardMedia
          alt={title}
          component="img"
          image={imageUrl}
          sx={{
            alignSelf: 'center',
            flexShrink: 0,
            height: DEFAULT_SERVICE_USAGE_PLACEHOLDER_HEIGHT,
            width: { xs: '100%', md: DEFAULT_SERVICE_USAGE_PLACEHOLDER_HEIGHT },
          }}
        />
        <Stack
          sx={{
            flexShrink: 0,
            justifyContent: 'center',
            minWidth: { md: 220 },
            p: 2,
          }}
        >
          <ListItem alignItems="flex-start" disablePadding>
            <ListItemAvatar>
              <Avatar icon={<FunctionsIcon />} label={title} />
            </ListItemAvatar>
            <ListItemText primary={totalModeLabel} secondary={title} />
          </ListItem>
          {!!(
            totalCalculation &&
            switchTotalModeMutation &&
            serviceUserId &&
            serviceUsagesGroupId &&
            resolvableFieldSettings
          ) && (
            <TotalModeSwitch
              automaticResult={totalCalculation.result}
              manualTotal={manualTotal}
              queryKeysToInvalidate={queryKeysToInvalidate ?? []}
              resolvableFieldSettings={resolvableFieldSettings}
              serviceUsagesGroupId={serviceUsagesGroupId}
              serviceUserId={serviceUserId}
              switchTotalModeMutation={switchTotalModeMutation}
              totalMode={totalCalculation.totalMode}
              updateManualTotalMutation={updateManualTotalMutation}
            />
          )}
        </Stack>
        {aside}
        <CardContent sx={{ alignContent: 'center', flexGrow: 1 }}>
          {pTotalCalculation ? (
            <ServiceUsagesTotalContent totalCalculation={pTotalCalculation} />
          ) : (
            <ConfidentialValue />
          )}
          {!!errorMessages?.length && (
            <AlertMessages
              messages={errorMessages}
              severity="error"
              sx={{ mt: 2 }}
            />
          )}
        </CardContent>
      </Stack>
    </Card>
  );
};
